import React from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from "@mui/material";

import useUser from "../../../../hooks/super/useUser";
import { UserType } from "../../../../utils/constants";
import { getUserRoleString } from "../../../../utils/format";

import { TOKENS, FormDrawer } from "../../../../components/pages/_new/super/design";

const fieldLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: TOKENS.fg2,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.5,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: TOKENS.bgSurface,
    fontSize: 14,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: TOKENS.border },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: TOKENS.primary },
};

function UserModal({ open, handleClose, user, loading, adminFlag }) {
  const { createUser, updateUser, createAdmin, updateAdmin } = useUser();

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        title: user?.title ? user?.title : "",
        firstName: user?.firstName ? user?.firstName : "",
        lastName: user?.lastName ? user?.lastName : "",
        email: user?.email ? user?.email : "",
        password: "",
        confirmPassword: "",
        role: undefined !== user?.role ? user?.role : adminFlag ? UserType.ADMIN : UserType.ORG_USER,
        verify: false,
      }}
      validationSchema={Yup.object().shape({
        title: Yup.string().max(255),
        firstName: Yup.string().max(255).required("First Name is required"),
        lastName: Yup.string().max(255).required("Last Name is required"),
        email: !user ? Yup.string().email("Must be a valid email").max(255).required("Email is required") : "",
        password: !user
          ? Yup.string()
              .min(8, "Must be at least 8 characters")
              .max(255)
              .matches(
                /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
                "Password must contain at least 8 characters, one uppercase, one number and one special case character"
              )
              .required("Password is required")
          : Yup.string()
              .min(8, "Must be at least 8 characters")
              .max(255)
              .matches(
                /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
                "Password must contain at least 8 characters, one uppercase, one number and one special case character"
              ),
        confirmPassword: Yup.string().test("passwords-match", "Passwords must match", function (value) {
          return this.parent.password === value;
        }),
        verify: Yup.bool().default(false),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          if (user) {
            if (adminFlag) {
              await updateAdmin(user?.id, values);
            } else {
              await updateUser(user?.id, values);
            }
          } else {
            if (adminFlag) {
              await createAdmin(values);
            } else {
              await createUser(values);
            }
          }
          handleClose();
          resetForm();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, resetForm, dirty }) => (
        <FormDrawer
          open={open}
          onClose={() => {
            handleClose();
            resetForm();
          }}
          title={user ? (adminFlag ? "Edit Administrator" : "Edit User") : adminFlag ? "Add Administrator" : "Add User"}
          submitLabel={user ? "Save changes" : "Create"}
          loading={isSubmitting || loading}
          canSubmit={!isSubmitting}
          onSubmit={handleSubmit}
          isDirty={dirty}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <form noValidate onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {errors.submit && (
                  <Alert severity="error" variant="outlined" sx={{ borderRadius: "10px" }}>
                    {errors.submit}
                  </Alert>
                )}

                {!user && (
                  <Box>
                    <Typography sx={fieldLabelSx}>Email *</Typography>
                    <TextField
                      type="text"
                      name="email"
                      value={values.email}
                      fullWidth
                      size="small"
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      sx={inputSx}
                    />
                  </Box>
                )}

                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={fieldLabelSx}>First Name *</Typography>
                    <TextField
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      fullWidth
                      size="small"
                      error={Boolean(touched.firstName && errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      sx={inputSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={fieldLabelSx}>Last Name *</Typography>
                    <TextField
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      fullWidth
                      size="small"
                      error={Boolean(touched.lastName && errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      sx={inputSx}
                    />
                  </Box>
                </Stack>

                <Box>
                  <Typography sx={fieldLabelSx}>Title</Typography>
                  <TextField
                    type="text"
                    name="title"
                    value={values.title}
                    fullWidth
                    size="small"
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Box>

                <Box>
                  <Typography sx={fieldLabelSx}>Role *</Typography>
                  <FormControl fullWidth>
                    <Select
                      name="role"
                      value={values.role}
                      size="small"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      sx={{
                        borderRadius: "10px",
                        fontSize: 14,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: TOKENS.border },
                      }}
                    >
                      {(adminFlag
                        ? [UserType.SUPER_ADMIN, UserType.ADMIN]
                        : [UserType.ORG_ADMIN, UserType.ORG_USER]
                      ).map((role) => (
                        <MenuItem value={role} key={role}>
                          {getUserRoleString(role)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography sx={fieldLabelSx}>Password {!user ? "*" : ""}</Typography>
                  <TextField
                    type="password"
                    name="password"
                    value={values.password}
                    fullWidth
                    size="small"
                    placeholder={user ? "Leave blank to keep current password" : ""}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Box>

                <Box>
                  <Typography sx={fieldLabelSx}>Confirm Password {!user ? "*" : ""}</Typography>
                  <TextField
                    type="password"
                    name="confirmPassword"
                    value={values.confirmPassword}
                    fullWidth
                    size="small"
                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Box>

                {!user && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="verify"
                        checked={values.verify}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        sx={{ color: TOKENS.fg2, "&.Mui-checked": { color: TOKENS.primary } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13.5, color: TOKENS.fg1 }}>
                        Mark as verified on creation
                      </Typography>
                    }
                  />
                )}
              </Stack>
            </form>
          )}
        </FormDrawer>
      )}
    </Formik>
  );
}

export default UserModal;
